import { setupInterceptors } from '@billing/app/http-client'
import { setupValidation } from '@billing/app/validation'
import { CommodityDialogUIStore } from '@billing/features/commodity/stores/commodity-dialog.ui-store'
import { CompanyDialogUIStore } from '@billing/features/company/stores/company-dialog.ui-store'
import { CompanyTableUIStore } from '@billing/features/company/stores/company-table.ui-store'
import { ContainerTurnoversUIStore } from '@billing/features/reports/stores/container-turnovers.ui-store'
import { CustomerBillingUIStore } from '@billing/features/reports/stores/customer-billing-report.ui-store'
import { HandlingServicesPreBillingUIStore } from '@billing/features/reports/stores/handling-services-prebilling-report.ui-store'
import { ReportsUIStore } from '@billing/features/reports/stores/reports.ui-store'
import { StorageReportUIStore } from '@billing/features/reports/stores/storage-report.ui-store'
import { VesselReportUIStore } from '@billing/features/reports/stores/vessel-report.ui-store'
import { CarrierVisitStore } from '@billing/stores/CarrierVisitStore'
import { CommodityStore } from '@billing/stores/CommodityStore'
import { CompanyStore } from '@billing/stores/CompanyStore'
import {
  AppStore,
  AuthStore,
  HubContext,
  MessageBus,
  appStore as appStoreObject,
  authStore as authStoreObject,
} from '@tom-ui/utils'
import { ReactNode, createContext, useContext } from 'react'
import { CatalogueDialogUIStore } from './features/commodity/stores/catalogue-dialog.ui-store'
import { PackageDialogUIStore } from './features/packaging/stores/package-dialog.ui-store'
import { PackageListUIStore } from './features/packaging/stores/package-list.ui-store'
import { DeliveryNoteReportUIStore } from './features/reports/stores/delivery-note-report.ui-store'
import { GCStoragePreBillingUIStore } from './features/reports/stores/gcStorage-prebilling-report.ui-store'
import { EventTypes } from './messages/eventsTypes'
import { CatalogueStore } from './stores/CatalogueStore'
import { PackageStore } from './stores/PackageStore'
import { TenantStore } from './stores/TenantStore'

export const messageBus = new MessageBus()
export const hubContext = new HubContext('/billingServiceHub', messageBus)
const listenedEvents: string[] = [EventTypes.CommodityRefresh]

listenedEvents.forEach(c => hubContext.registerPassThroughConnection(c))
hubContext.startConnection()

type IStore = {
  appStore: AppStore
  authStore: AuthStore
  companyStore: CompanyStore
  commodityStore: CommodityStore
  catalogueStore: CatalogueStore
  packageStore: PackageStore
  carrierVisitStore: CarrierVisitStore
  tenantStore: TenantStore
}

const appStore = appStoreObject
const authStore = authStoreObject
const companyStore = new CompanyStore()
const commodityStore = new CommodityStore()
const catalogueStore = new CatalogueStore()
const packageStore = new PackageStore()
const carrierVisitStore = new CarrierVisitStore()
const tenantStore = new TenantStore()

type IViewStore = {
  reportsUIStore: ReportsUIStore
  vesselReportUIStore: VesselReportUIStore
  storageReportUIStore: StorageReportUIStore
  companyDialogUIStore: CompanyDialogUIStore
  companyTableUIStore: CompanyTableUIStore
  commodityDialogUIStore: CommodityDialogUIStore
  catalogueDialogUIStore: CatalogueDialogUIStore
  packageDialogUIStore: PackageDialogUIStore
  packageListUIStore: PackageListUIStore
  containerTurnoversUIStore: ContainerTurnoversUIStore
  customerBillingUIStore: CustomerBillingUIStore
  gcPreBillingStorageUIStore: GCStoragePreBillingUIStore
  deliveryNoteReportUIStore: DeliveryNoteReportUIStore
  handlingServicesPrebillingUIStore: HandlingServicesPreBillingUIStore
}

const reportsUIStore = new ReportsUIStore()
const vesselReportUIStore = new VesselReportUIStore(carrierVisitStore)
const storageReportUIStore = new StorageReportUIStore()

const companyDialogUIStore = new CompanyDialogUIStore(companyStore)
const companyTableUIStore = new CompanyTableUIStore(companyStore)

const commodityDialogUIStore = new CommodityDialogUIStore(commodityStore)
const catalogueDialogUIStore = new CatalogueDialogUIStore(catalogueStore, commodityStore)

const packageDialogUIStore = new PackageDialogUIStore(packageStore)
const packageListUIStore = new PackageListUIStore(packageStore)

const containerTurnoversUIStore = new ContainerTurnoversUIStore()
const customerBillingUIStore = new CustomerBillingUIStore(companyStore)

const gcPreBillingStorageUIStore = new GCStoragePreBillingUIStore(companyStore)

const handlingServicesPrebillingUIStore = new HandlingServicesPreBillingUIStore(companyStore)

const deliveryNoteReportUIStore = new DeliveryNoteReportUIStore(
  carrierVisitStore,
  companyStore,
  tenantStore,
)

export interface IAppStore extends IStore, IViewStore {}

const store: IAppStore = {
  appStore,
  authStore,
  companyStore,
  commodityStore,
  catalogueStore,
  packageStore,
  carrierVisitStore,
  tenantStore,
  companyDialogUIStore,
  companyTableUIStore,
  commodityDialogUIStore,
  catalogueDialogUIStore,
  packageDialogUIStore,
  packageListUIStore,
  reportsUIStore,
  vesselReportUIStore,
  storageReportUIStore,
  containerTurnoversUIStore,
  customerBillingUIStore,
  gcPreBillingStorageUIStore,
  deliveryNoteReportUIStore,
  handlingServicesPrebillingUIStore,
}

setupValidation()
setupInterceptors(appStore)

const BillingServiceContext = createContext<IAppStore>({ ...store })

export const BillingServiceStoreProvider = ({ children }: { children: ReactNode }) => {
  return <BillingServiceContext.Provider value={store}>{children}</BillingServiceContext.Provider>
}

export const useBillingServiceStore = () => useContext(BillingServiceContext)
